import { updateProject } from '@/backend/updateProject'
import type { Eventually } from '@/data/clientUtils'
import { hubNameToProjectName } from '@/data/Hubs/hubName'
import { useHubStore } from '@/data/Hubs/useHubStore'
import { queryClient } from '@/modules/App/queryClient'
import { invariant } from '@/shared/utils/typeAssertions'
import { useMutation } from '@tanstack/vue-query'
import { toValue } from 'vue'

/**
 * Mutation for updating an existing hub
 */
export function useUpdateHubMutation(props: { workspaceId: Eventually<string> }) {
  const hubStore = useHubStore()

  return useMutation({
    mutationFn: async (params: { hubId: string; name: string; description: string }) => {
      const payload = {
        name: hubNameToProjectName(params.name),
        description: params.description,
      }

      const workspaceId = toValue(props.workspaceId)
      invariant(workspaceId)
      const response = await updateProject(workspaceId, params.hubId, payload)

      if (!response.ok) throw new Error(response.error.message)

      const hub = hubStore.hubs.get(params.hubId)
      invariant(hub, 'Hub not found')
      if (hub) {
        hub.name = params.name
        hub.description = params.description
      }
      return hub
    },
    onSettled(_response, _error, request) {
      const hubInvalidation = queryClient.invalidateQueries({
        queryKey: [props.workspaceId, 'hub', request.hubId],
      })
      const listInvalidation = queryClient.invalidateQueries({
        queryKey: [props.workspaceId, 'hubs'],
      })
      return Promise.all([hubInvalidation, listInvalidation])
    },
  })
}
